import React from "react";
import { Button, GridItem, useDisclosure } from "@chakra-ui/react";
import { format, isEqual, parse } from "date-fns";
import { IoAdd } from "react-icons/io5";
import { Form, Grid, Modal, NumberInput, Select } from "components";
import { useToast } from "duck";
import { FormValues, getValidation, useCreateVital } from "./duck";

interface CreateProps {
  patientId: number;
  timeIntervals: string[];
  disabledIntervals: string[];
  caseCreationDate: Date;
}

const Create: React.FC<CreateProps> = ({
  patientId,
  timeIntervals,
  disabledIntervals,
  caseCreationDate
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  const onSubmit = useCreateVital(patientId);

  const timeOptions = timeIntervals.map(el => {
    const timeVal = parse(el, "h:mm a", caseCreationDate);
    const newLabel = format(timeVal, "h:mm aaaa");
    return { value: `${timeVal}`, label: newLabel };
  });

  return (
    <>
      <Button leftIcon={<IoAdd />} onClick={onOpen}>
        Add
      </Button>
      <Modal title="Vitals" onClose={onClose} isOpen={isOpen} size="xl">
        <Form<FormValues>
          {...getValidation()}
          onSubmit={values =>
            onSubmit(values).then(() => {
              toast({
                status: "success",
                description: `Vitals for ${format(
                  values.timeMonitoring as Date,
                  "h:mm aaaa"
                )} are added successfully.`
              });
              onClose();
            })
          }
        >
          <Grid templateColumns={{ lg: "repeat(2, 1fr)" }} py={6}>
            <GridItem>
              <Select
                name="timeMonitoring"
                label="Time"
                options={timeOptions}
                placeholder="Select Time"
                isOptionDisabled={opt =>
                  disabledIntervals.some(interval =>
                    isEqual(new Date(opt.value), new Date(interval))
                  )
                }
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="heartRate"
                label="Heart Rate (HR)"
                placeholder="Heart Rate"
                min={0}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="respiratoryRate"
                label="Respiratory Rate (RR)"
                placeholder="Respiratory Rate"
                min={0}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="pulseOximetry"
                label="Pulse Oximetry (SpO2)"
                placeholder="Pulse Oximetry"
                min={0}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="endTidal"
                label="End-tidal CO2"
                placeholder="End-tidal CO2"
                min={0}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="systolicBloodPressure"
                label="Systolic Blood Pressure (SBP)"
                placeholder="Systolic Blood Pressure"
                min={0}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="meanBloodPressure"
                label="Mean Blood Pressure (MBP)"
                placeholder="Mean Blood Pressure"
                min={0}
              />
            </GridItem>
            <GridItem>
              <NumberInput
                name="diastolicBloodPressure"
                label="Diastolic Blood Pressure (DBP)"
                placeholder="Diastolic Blood Pressure"
                min={0}
              />
            </GridItem>
          </Grid>
          <Form.Controls submitText="Save">
            <Button variant="outline" colorScheme="gray" onClick={onClose}>
              Cancel
            </Button>
          </Form.Controls>
        </Form>
      </Modal>
    </>
  );
};

export default Create;
